import { useState, useRef, useEffect } from 'react';
import { B, radius, font, weight } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { Icon, Avatar, FieldLabel, clickable } from '../ui';

export const AssigneePicker = ({ selected, onChange, users = [], contacts = [], required = false, label = 'Assign To', searchPlaceholder = 'Search people to assign…' }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const rootRef = useRef(null);

  // Close the list on a real click outside the picker — NOT on input blur.
  // Closing on blur meant grabbing the list's scrollbar (which pulls focus off
  // the input) fired the close, slamming a long roster shut mid-scroll. That
  // made it impossible to reach people further down the list — i.e. to assign
  // everyone. Driving the close from where the user clicks fixes that: clicks
  // inside the picker (scrollbar, items, input) keep it open; only an outside
  // click dismisses it.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Ids are compared case-insensitively: the same UUID arrives uppercase from a
  // SELECT and lowercase from a client or a JWT claim, and an exact compare shows
  // a selected person as unticked and lets them be added a second time.
  const sameId = (a, b) => String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase();
  const matches = (a, b) => a.type === b.type && sameId(a.id, b.id);

  const roster = [
    ...users.filter(u => u.isActive).map(u => ({
      type: 'user', id: u.userID, name: u.displayName,
      role: u.role, isInternal: u.isInternal,
    })),
    // Task assignees reference a ContactDetails row (a person), not the
    // Contacts company row. The /contacts list exposes that as
    // `primaryContactDetailID` (+ `primaryPersonName`/`companyName`) — using
    // `c.contactID` here sent the company PK as a ContactDetailID, which is not
    // a valid assignee and made contact assignments fail / show blank.
    ...contacts
      .filter(c => c.primaryContactDetailID)
      .map(c => ({
        type: 'contact', id: c.primaryContactDetailID,
        name: c.primaryPersonName || c.companyName,
        role: c.primaryPersonTitle || c.companyName || 'Contact', isInternal: false,
      })),
  ];
  const allPeople = roster.filter(p => !search.trim() || (p.name || '').toLowerCase().includes(search.toLowerCase()));

  // A caller may seed the selection with bare ids (a task drafted from a note
  // carries only `{ type, id }`), so the name and role are filled in from the
  // roster here. Without this the chip renders blank — no name, no initials —
  // which reads as a broken assignee rather than a real one. Inactive users are
  // looked up too, since a seeded id is still assigned even if the roster
  // filter above hides them from the list.
  const fullRoster = [
    ...users.map(u => ({ type: 'user', id: u.userID, name: u.displayName, role: u.role, isInternal: u.isInternal })),
    ...roster.filter(p => p.type === 'contact'),
  ];
  const shown = selected.map(s => {
    if (s.name) return s;
    const hit = fullRoster.find(p => matches(p, s));
    return hit ? { ...s, name: hit.name, role: hit.role, isInternal: hit.isInternal } : { ...s, name: users.length ? 'Unknown user' : 'Loading…' };
  });

  const toggle = item => {
    const already = selected.find(s => matches(s, item));
    onChange(already
      ? selected.filter(s => !matches(s, item))
      : [...selected, item]);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', marginBottom: 16 }}>
      <FieldLabel required={required}>{label}</FieldLabel>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {shown.map(item => (
            <div key={`${item.type}-${item.id}`} style={{ display: 'flex', alignItems: 'center', gap: 6, background: item.type === 'contact' ? B.tealDk : B.navy, color: B.white, borderRadius: radius.pill, padding: '4px 10px 4px 6px', fontSize: font.sm }}>
              <Avatar initials={fmt.initials(item.name)} size={20} color="rgba(255,255,255,0.22)" textColor={B.white} />
              {item.name}
              {item.type === 'contact' && <span style={{ fontSize: 9, opacity: 0.7 }}>C</span>}
              <span onClick={() => toggle(item)} style={{ cursor: 'pointer', marginLeft: 2, opacity: 0.7, display: 'inline-flex' }}><Icon name="close" size={13} /></span>
            </div>
          ))}
        </div>
      )}
      <input
        type="text"
        value={search}
        placeholder={selected.length ? `${selected.length} selected — search to add more` : searchPlaceholder}
        onFocus={() => setOpen(true)}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: `1px solid ${required && selected.length === 0 ? B.red : B.border}`, borderRadius: radius.md, fontSize: font.base, color: B.text, fontFamily: 'inherit', outline: 'none' }}
      />
      {required && selected.length === 0 && (
        <div style={{ marginTop: 4, fontSize: font.xs, color: B.red }}>At least one assignee is required.</div>
      )}
      {open && (
        <div style={{ position: 'absolute', bottom: 'calc(100% + 2px)', top: 'auto', left: 0, right: 0, zIndex: 300, border: `1px solid ${B.border}`, borderRadius: radius.lg, background: B.surface, boxShadow: B.shadowMd, maxHeight: 220, overflowY: 'auto' }}>
          {allPeople.length === 0 && <div style={{ padding: '8px 12px', fontSize: font.sm, color: B.textLt }}>No matching people</div>}
          {allPeople.map(item => {
            const isSel = !!selected.find(s => matches(s, item));
            return (
              <div key={`${item.type}-${item.id}`} {...clickable(() => toggle(item), { role: 'option' })}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: isSel ? B.tealLt : 'transparent', borderBottom: `1px solid ${B.divider}` }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = B.hover; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ width: 18, height: 18, borderRadius: radius.sm, border: `2px solid ${isSel ? B.navy : B.border}`, background: isSel ? B.navy : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: B.white }}>
                  {isSel && <Icon name="check" size={11} />}
                </div>
                <Avatar initials={fmt.initials(item.name)} size={28} color={item.type === 'contact' ? B.tealDk : (item.isInternal ? B.navy : B.tealDk)} />
                <div>
                  <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text }}>{item.name}</div>
                  <div style={{ fontSize: font.xs, color: B.textLt }}>{item.role}{item.type === 'contact' ? ' · Contact' : (!item.isInternal ? ' · External' : '')}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
